// src/pages/reports/MessageCenterSummaryPage.jsx
import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  PlusCircle,
  ShoppingCart,
  Truck,
  CheckCircle,
  Target,
  Wallet,
  Loader2,
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import FilterPanel from '../../components/common/FilterPanel/FilterPanel';
import ReportDatePresetFilter from '../../components/common/ReportDatePresetFilter/ReportDatePresetFilter';
import StatsCards from '../../components/common/StatsCards/StatsCards';
import Gauge from '../../components/common/Gauge/Gauge';
import InfoIcon from '../../components/common/InfoIcon/InfoIcon';
import {
  getMessageCenterSummaryKpis,
  getMessageCenterShiftTodayAchievement,
  getMessageCenterShiftLast7DaysTrend,
} from '../../services/api/messageCenterSummaryReport';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency, formatCurrencyCompact } from '../../utils/currencyUtil';
import styles from './MessageCenterSummaryPage.module.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const pad = (n) => String(n).padStart(2, '0');
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const getDefaultDateRange = () => {
  const today = new Date();
  const from = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    dateFrom: fmt(from),
    dateTo: fmt(today),
  };
};

const buildAchievedVsTargetChartData = (series, t) => ({
  labels: series.map((s) => s.label),
  datasets: [
    { label: t('report.achieved'), data: series.map((s) => s.achieved), backgroundColor: '#10b981' },
    { label: t('report.target'), data: series.map((s) => s.target), backgroundColor: '#93c5fd' },
  ],
});

const achievedVsTargetChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 900, easing: 'easeOutQuart' },
  plugins: {
    legend: { position: 'top' },
    tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.raw)}` } },
  },
  scales: {
    y: { beginAtZero: true, title: { display: true, text: 'LKR' } },
    x: { title: { display: true, text: 'Date' } },
  },
};

const MessageCenterSummaryPage = () => {
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({ date: getDefaultDateRange() });

  const [kpis, setKpis] = useState(null);
  const [isLoadingKpis, setIsLoadingKpis] = useState(false);

  const [shiftAchievements, setShiftAchievements] = useState([]);
  const [isLoadingShifts, setIsLoadingShifts] = useState(true);
  const [shiftTrends, setShiftTrends] = useState([]);

  // Effect A — page-filtered widgets (re-runs whenever the date filter changes)
  useEffect(() => {
    if (filters.date?.dateFrom && filters.date?.dateTo) {
      loadFilteredWidgets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date?.dateFrom, filters.date?.dateTo]);

  // Effect B — fixed context widget (always trailing 7 days), runs once on mount only
  useEffect(() => {
    loadFixedTrendWidgets();
  }, []);

  const loadFilteredWidgets = async () => {
    const params = { dateFrom: filters.date.dateFrom, dateTo: filters.date.dateTo };
    try {
      setIsLoadingKpis(true);
      setIsLoadingShifts(true);
      const [data, shiftData] = await Promise.all([
        getMessageCenterSummaryKpis(params),
        getMessageCenterShiftTodayAchievement(params),
      ]);
      setKpis(data);
      setShiftAchievements(shiftData.shiftTypes || []);
    } catch (err) {
      showError(err.response?.data?.message || t('report.messageCenterSummary.failedToLoad'));
    } finally {
      setIsLoadingKpis(false);
      setIsLoadingShifts(false);
    }
  };

  const loadFixedTrendWidgets = async () => {
    try {
      const trendData = await getMessageCenterShiftLast7DaysTrend();
      setShiftTrends(trendData.shiftTypes || []);
    } catch (err) {
      showError(err.response?.data?.message || t('report.messageCenterSummary.failedToLoad'));
    }
  };

  const handleFilterChange = (values) => {
    setFilters((prev) => ({ ...prev, ...values }));
  };

  const handleFilterClear = () => {
    setFilters({ date: getDefaultDateRange() });
  };

  const filterDefs = [
    {
      key: 'date',
      label: t('report.dateRange'),
      type: 'custom',
      gridColumn: '1 / -1',
      render: (value, onChange) => <ReportDatePresetFilter value={value || {}} onChange={onChange} />,
    },
  ];

  const buildKpiStats = () => {
    if (!kpis) return [];
    return [
      {
        key: 'createdOrders',
        label: t('report.messageCenterSummary.createdOrders'),
        tooltip: t('report.messageCenterSummary.createdOrdersTooltip'),
        icon: PlusCircle,
        color: '#0ea5e9',
        value: kpis.createdOrders.count,
        format: (v) => {
          const compact = formatCurrencyCompact(kpis.createdOrders.value);
          return (
            <div className={styles.cardValueStack}>
              <div>{v.toLocaleString()} ({kpis.createdOrders.products.toLocaleString()})</div>
              <div className={styles.cardSubValue} title={compact.title}>LKR {compact.display}</div>
            </div>
          );
        },
      },
      {
        key: 'convertOrders',
        label: t('report.messageCenterSummary.convertOrders'),
        tooltip: t('report.messageCenterSummary.convertOrdersTooltip'),
        icon: ShoppingCart,
        color: '#3b82f6',
        value: kpis.convertOrders.count,
        format: (v) => {
          const compact = formatCurrencyCompact(kpis.convertOrders.value);
          return (
            <div className={styles.cardValueStack}>
              <div>{v.toLocaleString()} ({kpis.convertOrders.products.toLocaleString()})</div>
              <div className={styles.cardSubValue} title={compact.title}>LKR {compact.display}</div>
            </div>
          );
        },
      },
      {
        key: 'deliveredOrders',
        label: t('report.messageCenterSummary.deliveredOrders'),
        tooltip: t('report.messageCenterSummary.deliveredOrdersTooltip'),
        icon: Truck,
        color: '#8b5cf6',
        value: kpis.deliveredOrders.count,
        format: (v) => {
          const compact = formatCurrencyCompact(kpis.deliveredOrders.value);
          return (
            <div className={styles.cardValueStack}>
              <div>{v.toLocaleString()} ({kpis.deliveredOrders.products.toLocaleString()})</div>
              <div className={styles.cardSubValue} title={compact.title}>LKR {compact.display}</div>
            </div>
          );
        },
      },
      {
        key: 'settledOrders',
        label: t('report.messageCenterSummary.settledOrders'),
        tooltip: t('report.messageCenterSummary.settledOrdersTooltip'),
        icon: CheckCircle,
        color: '#10b981',
        value: kpis.settledOrders.count,
        format: (v) => {
          const compact = formatCurrencyCompact(kpis.settledOrders.value);
          return (
            <div className={styles.cardValueStack}>
              <div>{v.toLocaleString()} ({kpis.settledOrders.products.toLocaleString()})</div>
              <div className={styles.cardSubValue} title={compact.title}>LKR {compact.display}</div>
            </div>
          );
        },
      },
      {
        key: 'achievedVsTarget',
        label: t('report.messageCenterSummary.achievedVsTarget'),
        tooltip: t('report.messageCenterSummary.achievedVsTargetTooltip'),
        icon: Target,
        color: '#f59e0b',
        value: kpis.achievedPercentage,
        format: (v) => {
          const achievedCompact = formatCurrencyCompact(kpis.achieved);
          const targetCompact = formatCurrencyCompact(kpis.target);
          return (
            <div className={styles.cardValueStack}>
              <div>{v}%</div>
              <div className={styles.cardSubValue}>
                <span title={achievedCompact.title}>LKR {achievedCompact.display}</span> / <span title={targetCompact.title}>LKR {targetCompact.display}</span>
              </div>
            </div>
          );
        },
      },
      {
        key: 'totalCommission',
        label: t('report.messageCenterSummary.totalCommission'),
        tooltip: t('report.messageCenterSummary.totalCommissionTooltip'),
        icon: Wallet,
        color: '#22c55e',
        value: kpis.commission,
        format: (v) => formatCurrency(v),
      },
    ];
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('report.messageCenterSummary.title')}
        description={t('report.messageCenterSummary.subtitle')}
        icon={MessageSquare}
      />

      <FilterPanel
        filters={filterDefs}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
        show={showFilters}
        onToggle={() => setShowFilters((s) => !s)}
      />

      <div className={styles.sectionHeader}>
        <h2>{t('report.messageCenterSummary.kpiSectionTitle')}</h2>
        <InfoIcon text={t('report.messageCenterSummary.kpiSectionTooltip')} />
      </div>
      <StatsCards
        loading={isLoadingKpis && !kpis}
        stats={buildKpiStats()}
        minColumnWidth="220px"
      />

      <div className={styles.shiftGaugeGrid}>
        {shiftAchievements.map((shift) => (
          <div key={shift.shiftTypeId} className={styles.shiftCard}>
            <div className={styles.cardHeader}>
              <h3>{t('report.messageCenterSummary.rangeShiftTitle', { shiftTypeName: shift.shiftTypeName })}</h3>
              <InfoIcon text={t('report.messageCenterSummary.todayShiftTooltip')} />
            </div>
            <div className={styles.shiftCardBody}>
              <div className={styles.shiftDetails}>
                <p><strong>{t('report.messageCenterSummary.leader')}:</strong> {shift.leaders?.map((l) => l.name).join(', ') || shift.leader?.name || '—'}</p>
                <p><strong>{t('report.messageCenterSummary.createdOrders')}:</strong> {shift.created.count.toLocaleString()}</p>
                <p><strong>{t('report.messageCenterSummary.createdProducts')}:</strong> {shift.created.products.toLocaleString()}</p>
                <p><strong>{t('report.messageCenterSummary.createdValue')}:</strong> {formatCurrency(shift.created.value)}</p>
                <p><strong>{t('report.messageCenterSummary.totalOrders')}:</strong> {shift.converted.count.toLocaleString()}</p>
                <p><strong>{t('report.messageCenterSummary.totalProducts')}:</strong> {shift.converted.products.toLocaleString()}</p>
                <p><strong>{t('report.messageCenterSummary.totalValue')}:</strong> {formatCurrency(shift.converted.value)}</p>
                <p><strong>{t('report.messageCenterSummary.targetValue')}:</strong> {formatCurrency(shift.target)}</p>
                <p><strong>{t('report.messageCenterSummary.commission')}:</strong> {formatCurrency(shift.commission)}</p>
              </div>
              <Gauge
                value={shift.achieved}
                target={shift.target}
                label={t('report.achieved')}
                formatValue={(v) => formatCurrency(v)}
              />
            </div>
          </div>
        ))}
        {!isLoadingShifts && shiftAchievements.length === 0 && (
          <p className={styles.emptyText}>{t('report.messageCenterSummary.noShiftTypesFound')}</p>
        )}
        {isLoadingShifts && shiftAchievements.length === 0 && (
          <div className={styles.loadingRow}><Loader2 className={styles.spinner} size={24} /></div>
        )}
      </div>

      <div className={styles.shiftTrendGrid}>
        {shiftTrends.map((shift) => (
          <div key={shift.shiftTypeId} className={styles.chartCard}>
            <div className={styles.cardHeader}>
              <h3>{t('report.messageCenterSummary.last7DaysTitle', { shiftTypeName: shift.shiftTypeName })}</h3>
              <InfoIcon text={t('report.messageCenterSummary.last7DaysTooltip')} />
            </div>
            <div className={styles.chartWrapper}>
              <Bar data={buildAchievedVsTargetChartData(shift.series, t)} options={achievedVsTargetChartOptions} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MessageCenterSummaryPage;
